import React, { useState, useEffect } from "react";
import { Ripple } from "primereact/ripple";
import { Dialog } from 'primereact/dialog';
import { Button } from "primereact/button";
import { Toast } from 'primereact/toast';
import { Nullable } from "primereact/ts-helpers";
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import TextInput from "../../../../../Components/TextInput";
import FileInput from "../../../../../Components/FileInput";
import DatePicker from "../../../../../Components/DatePicker";
import { formatDate, parseStringToDate } from "../../../../../Utils/getDate";
import { checkFileOthers, checkText } from "../../../../../Utils/Validator";
import {
    DischargeConditions,
    dischargeConditionTypes,
    DischargeConditionTypes,
    Status,
    statusList
} from "../../../../../Controllers/Types/Project/DefaultService/DischargeConditionInterface";
import { dischargeConditionCreateAPI, dischargeConditionUpdateAPI } from "../../../../../Api/Project/DefaultServices/DischargeConditionApi";
import Textarea from "../../../../../Components/TextArea";


interface DischargeConditionModalProps {
    isOpen: boolean;
    dataState: 'Add' | 'Edit';
    onClose: () => void;
    onFetchData: () => void;
    toast: React.RefObject<Toast>;
    serviceId: number;
    selectedDisConData?: DischargeConditions | null;
}

const DischargeConditionModal: React.FC<DischargeConditionModalProps> = ({
    isOpen,
    dataState,
    onClose,
    onFetchData,
    toast,
    serviceId,
    selectedDisConData
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [updateHasChanges, setUpdateHasChanges] = useState<boolean>(false);

    /********************************* Form variables *************************************/
    const [dischargeConditionType, setDischargeConditionType] = useState<DischargeConditionTypes | null>(null);
    const [referenceNumber, setReferenceNumber] = useState<string>('');
    const [shortDescription, setShortDescription] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [status, setStatus] = useState<Status | null>(null);
    const [validationDate, setValidationDate] = useState<Nullable<Date>>(null);
    const [decisionDate, setDecisionDate] = useState<Nullable<Date>>(null);
    const [drawingFile, setDrawingFile] = useState<File | null>(null);
    const [drawingFileName, setDrawingFileName] = useState<string>('');
    const [decisionNoticeFile, setDecisionNoticeFile] = useState<File | null>(null);
    const [decisionNoticeFileName, setDecisionNoticeFileName] = useState<string>('');

    /* Error message */
    const [dischargeConditionTypeError, setDischargeConditionTypeError] = useState<string>('');
    const [referenceNumberError, setReferenceNumberError] = useState<string>('');
    const [descriptionError, setDescriptionError] = useState<string>('');
    const [shortDescriptionError, setShortDescriptionError] = useState<string>('');
    const [statusError, setStatusError] = useState<string>('');
    const [validationDateError, setValidationDateError] = useState<string>('');
    const [decisionDateError, setDecisionDateError] = useState<string>('');
    const [drawingFileError, setDrawingFileError] = useState<string>('');
    const [decisionNoticeFileError, setDecisionNoticeFileError] = useState<string>('');
    /* ********************************************************************************** */

    useEffect(() => {
        if (dataState === "Add" && selectedDisConData === null) {
            setDischargeConditionType(null);
            setReferenceNumber('');
            setDescription('');
            setStatus(null);
            setValidationDate(null);
            setDecisionDate(null);
        } else {
            const conType = selectedDisConData?.type! ? dischargeConditionTypes.filter(oneType => oneType.type === selectedDisConData?.type!)[0] : null;
            const conStatus = selectedDisConData?.status! ? statusList.filter(oneStatus => oneStatus.name === selectedDisConData?.status!)[0] : null;
            setDischargeConditionType(conType);
            setReferenceNumber(selectedDisConData?.reference_no || '');
            setShortDescription(selectedDisConData?.short_description || '');
            setDescription(selectedDisConData?.description || '');
            setStatus(conStatus);
            setValidationDate(parseStringToDate(selectedDisConData?.validation_date! || ''));
            setDecisionDate(parseStringToDate(selectedDisConData?.decision_date! || ''));
        }
        setDrawingFile(null);
        setDrawingFileName('');
        setDecisionNoticeFile(null);
        setDecisionNoticeFileName('');
    }, [dataState, selectedDisConData]);

    const handleResetFields = () => {
        setDischargeConditionType(null);
        setReferenceNumber('');
        setShortDescription('');
        setDescription('');
        setStatus(null);
        setValidationDate(null);
        setDecisionDate(null);
        setDrawingFile(null);
        setDrawingFileName('');
        setDecisionNoticeFile(null);
        setDecisionNoticeFileName('');
    }

    const handleResetErrors = () => {
        setDischargeConditionTypeError('');
        setReferenceNumberError('');
        setDescriptionError('');
        setShortDescriptionError('');
        setStatusError('');
        setValidationDateError('');
        setDecisionDateError('');
        setDrawingFileError('');
        setDecisionNoticeFileError('');
    }

    const handleCloseModal = () => {
        onClose();
        handleResetFields();
        handleResetErrors();
    }

    function checkCredentials(validationDateDateOnly: string, decisionDateDateOnly: string): boolean {
        const isDischargeConditionTypeValid = checkText(dischargeConditionType?.type || '', true, setDischargeConditionTypeError);
        const isReferenceNumberValid = checkText(referenceNumber, true, setReferenceNumberError);
        let isShortDescriptionValid = checkText(shortDescription, true, setShortDescriptionError);

        if (isShortDescriptionValid && shortDescription.length > 100) {
            setShortDescriptionError('The short description should be within 100 characters.');
            isShortDescriptionValid = false;
        }

        const isDescriptionValid = checkText(description, true, setDescriptionError);
        const isStatusValid = checkText(status?.name || '', false, setStatusError);
        const isValidationDateValid = checkText(validationDateDateOnly, false, setValidationDateError);
        const isDecisionDateValid = checkText(decisionDateDateOnly, false, setDecisionDateError);
        const isDrawingFileValid = checkFileOthers(drawingFile!, false, setDrawingFileError);
        const isDecisionNoticeFileValid = checkFileOthers(decisionNoticeFile!, false, setDecisionNoticeFileError);

        return (
            isDischargeConditionTypeValid &&
            isReferenceNumberValid &&
            isShortDescriptionValid &&
            isDescriptionValid &&
            isStatusValid &&
            isValidationDateValid &&
            isDecisionDateValid &&
            isDrawingFileValid &&
            isDecisionNoticeFileValid
        );
    }

    /* ********************************************* Create ************************************** */
    const handleCreateDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        const validationDateDateOnly = formatDate(validationDate || null);
        const decisionDateDateOnly = formatDate(decisionDate || null);

        if (checkCredentials(validationDateDateOnly, decisionDateDateOnly)) {
            setLoading(true);
            const data = await dischargeConditionCreateAPI(
                Number(serviceId),
                dischargeConditionType?.type || '',
                validationDateDateOnly!,
                referenceNumber!,
                decisionDateDateOnly!,
                shortDescription,
                description,
                status?.name || '',
                drawingFile!,
                decisionNoticeFile!
            );
            setLoading(false);

            if (data.statusCode === 200) {
                onFetchData();
                handleCloseModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Discharge condition document added successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error adding discharge condition document.',
                        life: 3000
                    });
                }
            }
        }
    }
    /* ********************************************* Create ************************************** */

    /* ********************************************* Update ************************************** */
    useEffect(() => {
        if (selectedDisConData) {
            if (dischargeConditionType?.type !== selectedDisConData?.type) {
                setUpdateHasChanges(true);
            } else if (referenceNumber !== selectedDisConData?.reference_no) {
                setUpdateHasChanges(true);
            } else if (shortDescription !== selectedDisConData?.short_description) {
                setUpdateHasChanges(true);
            } else if (description !== selectedDisConData?.description) {
                setUpdateHasChanges(true);
            } else if (status?.name !== selectedDisConData?.status) {
                setUpdateHasChanges(true);
            } else if (formatDate(validationDate!) !== selectedDisConData?.validation_date) {
                setUpdateHasChanges(true);
            } else if (formatDate(decisionDate!) !== selectedDisConData?.decision_date) {
                setUpdateHasChanges(true);
            } else if (drawingFile !== null) {
                setUpdateHasChanges(true);
            } else if (decisionNoticeFile !== null) {
                setUpdateHasChanges(true);
            } else {
                setUpdateHasChanges(false);
            }
        }
    }, [
        selectedDisConData,
        dischargeConditionType,
        referenceNumber,
        description,
        shortDescription,
        status,
        validationDate,
        decisionDate,
        drawingFile,
        decisionNoticeFile
    ]);

    const handleUpdateDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        const validationDateDateOnly = formatDate(validationDate || null);
        const decisionDateDateOnly = formatDate(decisionDate || null);

        if (checkCredentials(validationDateDateOnly, decisionDateDateOnly)) {
            setLoading(true);
            const data = await dischargeConditionUpdateAPI(
                Number(selectedDisConData?.id),
                dischargeConditionType?.type || '',
                validationDateDateOnly!,
                referenceNumber!,
                decisionDateDateOnly!,
                shortDescription,
                description,
                status?.name || '',
                drawingFile!,
                decisionNoticeFile!
            );
            setLoading(false);

            if (data.statusCode === 200) {
                onFetchData();
                handleCloseModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Discharge condition document updated successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating discharge condition document.',
                        life: 3000
                    });
                }
            }
        }
    }
    /* ********************************************* Update ************************************** */

    /******************************* For drawing file upload ********************************/
    const handleDrawingFileUpload = (file: File) => {
        setDrawingFile(file);
    };

    const handleClearDrawingFile = () => {
        setDrawingFile(null);
        setDrawingFileName('');
    };
    /****************************************************************************************/


    /*************************** For Decision notice file upload ****************************/
    const handleDecisionNoticeFileUpload = (file: File) => {
        setDecisionNoticeFile(file);
    };

    const handleClearDecisionNoticeFile = () => {
        setDecisionNoticeFile(null);
        setDecisionNoticeFileName('');
    };
    /****************************************************************************************/


    /************************************* For status ***************************************/

    /* For showing statuses options in the dropdown list */
    const statusOptionTemplate = (option: Status) => {
        return (
            <div className="d-flex align-items-center w-100 justify-content-between">
                <div>{option.name}</div>
                <div className="color_display" style={{ backgroundColor: option.colorCode }}></div>
            </div>
        );
    };
    /****************************************************************************************/


    /* DischargeCondition Modal header */
    const detailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp;discharge condition details
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const detailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateDetails : handleUpdateDetails}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !updateHasChanges}
            />
        </div>
    );
    /*  */


    /* Clear validation messages */
    useEffect(() => {
        checkText(dischargeConditionType?.type!, false, setDischargeConditionTypeError);
    }, [dischargeConditionType]);

    useEffect(() => {
        checkText(referenceNumber, false, setReferenceNumberError);
    }, [referenceNumber]);

    useEffect(() => {
        checkText(shortDescription, false, setShortDescriptionError);
    }, [shortDescription]);

    useEffect(() => {
        checkText(description, false, setDescriptionError);
    }, [description]);

    useEffect(() => {
        checkText(status?.name!, false, setStatusError);
    }, [status]);

    useEffect(() => {
        checkText(formatDate(validationDate!), false, setValidationDateError);
    }, [validationDate]);

    useEffect(() => {
        checkText(formatDate(decisionDate!), false, setDecisionDateError);
    }, [decisionDate]);

    useEffect(() => {
        checkFileOthers(drawingFile!, false, setDrawingFileError);
    }, [drawingFile]);

    useEffect(() => {
        checkFileOthers(decisionNoticeFile!, false, setDecisionNoticeFileError);
    }, [decisionNoticeFile]);
    /*  */


    return (
        <Dialog
            header={detailModalHeader}
            footer={detailModalFooter}
            headerClassName="custom_modal_header"
            visible={isOpen}
            className="custom_modal_dialog modal_dialog_md"
            dismissableMask
            onHide={handleCloseModal}
        >
            <div className="custom_modal_body">
                <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                    <div className="row">
                        {/* Discharge condition type */}
                        <div className="col-12 col-sm-6">
                            <div className="page_form_group">
                                <label htmlFor="dischargeConditionType" className="custom_form_label is_required">Discharge condition type</label>
                                <Dropdown
                                    id="dischargeConditionType"
                                    value={dischargeConditionType}
                                    onChange={(e: DropdownChangeEvent) => setDischargeConditionType(e.value)}
                                    options={dischargeConditionTypes}
                                    optionLabel="type"
                                    placeholder="Select type"
                                    className="form_dropdown w-100"
                                    showClear
                                />

                                {dischargeConditionTypeError &&
                                    <small className="form_error_msg">{dischargeConditionTypeError}</small>
                                }
                            </div>
                        </div>

                        {/* Reference no */}
                        <div className="col-12 col-sm-6">
                            <TextInput
                                id="referenceNumber"
                                label="Reference no"
                                labelHtmlFor="referenceNumber"
                                required={true}
                                value={referenceNumber}
                                placeholder="Reference no"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReferenceNumber(e.target.value)}
                                error={referenceNumberError}
                            />
                        </div>

                        {/* Short Description */}
                        <div className="col-12">
                            <TextInput
                                id="shortDescription"
                                label="Short description"
                                labelHtmlFor="shortDescription"
                                required={true}
                                value={shortDescription}
                                placeholder="Short description"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShortDescription(e.target.value)}
                                error={shortDescriptionError}
                                maxLength={100}
                            />
                        </div>

                        {/* Description */}
                        <div className="col-12">
                            <Textarea
                                id="description"
                                label="Description"
                                labelHtmlFor="description"
                                required={true}
                                value={description}
                                placeholder="Description"
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                                error={descriptionError}
                            />
                        </div>

                        {/* Status */}
                        <div className="col-12 col-sm-6">
                            <div className="page_form_group">
                                <label htmlFor="status" className="custom_form_label">Status</label>
                                <Dropdown
                                    id="status"
                                    value={status}
                                    onChange={(e: DropdownChangeEvent) => setStatus(e.value)}
                                    options={statusList}
                                    optionLabel="name"
                                    itemTemplate={statusOptionTemplate}
                                    panelClassName="status_option_panel"
                                    placeholder="Select status"
                                    className="form_dropdown w-100"
                                    showClear
                                />

                                {statusError &&
                                    <small className="form_error_msg">{statusError}</small>
                                }
                            </div>
                        </div>

                        {/* Validation date */}
                        <div className="col-12 col-sm-6">
                            <DatePicker
                                id="validationDate"
                                label="Validation date"
                                labelHtmlFor="validationDate"
                                required={false}
                                value={validationDate}
                                placeholder="dd/mm/yyyy"
                                onChange={setValidationDate}
                                error={validationDateError}
                                showClear
                                onClear={() => setValidationDate(null)}
                            />
                        </div>

                        {/* Decision date */}
                        <div className="col-12 col-sm-6">
                            <DatePicker
                                id="validationDate"
                                label="Decision date"
                                labelHtmlFor="validationDate"
                                required={false}
                                value={decisionDate}
                                placeholder="dd/mm/yyyy"
                                onChange={setDecisionDate}
                                error={decisionDateError}
                                showClear
                                onClear={() => setDecisionDate(null)}
                            />
                        </div>

                        {/* Drawing */}
                        <div className="col-12 col-sm-6">
                            <FileInput
                                id="drawingFile"
                                label="Drawing"
                                labelHtmlFor="drawingFile"
                                required={false}
                                value={drawingFileName}
                                onUpload={handleDrawingFileUpload}
                                onClear={handleClearDrawingFile}
                                toast={toast}
                                error={drawingFileError}
                                containerClassName="mb-sm-0"
                            />
                        </div>

                        {/* Decision notice */}
                        <div className="col-12 col-sm-6">
                            <FileInput
                                id="decisionNoticeFile"
                                label="Decision notice"
                                labelHtmlFor="decisionNoticeFile"
                                required={false}
                                value={decisionNoticeFileName}
                                onUpload={handleDecisionNoticeFileUpload}
                                onClear={handleClearDecisionNoticeFile}
                                toast={toast}
                                error={decisionNoticeFileError}
                                containerClassName="mb-0"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

export default DischargeConditionModal;